/**
 * Account Management Page
 *
 * Main page for managing user account settings, profile, preferences, and security
 */

import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UnifiedHeader } from '../../components/UnifiedHeader';
import { UnifiedFooter } from '../../components/UnifiedFooter';
import { SEOHead } from '../../components/SEOHead';
import { AccountTabs } from '../../components/account/AccountTabs';
import { ProfileHeader } from '../../components/account/ProfileHeader';
import { PersonalInfoForm } from '../../components/account/PersonalInfoForm';
import { SettingsPanel } from '../../components/account/SettingsPanel';
import { SecurityPanel } from '../../components/account/SecurityPanel';
import { TestHistorySummary } from '../../components/account/TestHistorySummary';
import { PaymentHistory } from '@vsat/shared/components';

type TabType = 'personal' | 'security' | 'settings' | 'history' | 'payment';

interface LocationState {
  activeTab?: TabType;
}

export function AccountManagementPage() {
  const location = useLocation();
  const locationState = location.state as LocationState | null;
  const [activeTab, setActiveTab] = useState<TabType>(locationState?.activeTab || 'personal');
  const { t } = useTranslation();

  // Update active tab if provided in location state (e.g., from SchoolInfoReminder)
  useEffect(() => {
    if (locationState?.activeTab) {
      setActiveTab(locationState.activeTab);
    }
  }, [locationState]);

  return (
    <div className='bg-background-light dark:bg-background-dark text-gray-900 dark:text-gray-100 transition-colors duration-200 min-h-screen flex flex-col'>
      <SEOHead
        title='Quản lý tài khoản | Hướng nghiệp CAT'
        description='Quản lý thông tin tài khoản cá nhân - Hướng nghiệp CAT.'
        noindex
      />
      <UnifiedHeader />
      <main className='flex-1 pt-20 pb-12 lg:pt-24 bg-gray-50 dark:bg-gray-900'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
          {/* Page Header */}
          <div className='mb-8'>
            <h1 className='text-3xl font-bold text-gray-900 dark:text-white mb-2'>
              {t('account.title')}
            </h1>
            <p className='text-gray-500 dark:text-gray-400'>{t('account.subtitle')}</p>
          </div>

          {/* Tabs Navigation */}
          <AccountTabs activeTab={activeTab} onTabChange={setActiveTab} />

          <div className='space-y-8 mt-8'>
            {/* Profile Header Card - shown on all tabs */}
            <ProfileHeader />

            {/* Tab Content */}
            {activeTab === 'personal' && <PersonalInfoForm />}
            {activeTab === 'security' && <SecurityPanel />}
            {activeTab === 'settings' && <SettingsPanel />}
            {activeTab === 'history' && <TestHistorySummary />}
            {activeTab === 'payment' && <PaymentHistory />}
          </div>
        </div>
      </main>
      <UnifiedFooter />
    </div>
  );
}
